import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { eyeIcon, eyeSlashIcon } from 'assets';
import './OpPasswordBox.css';
import { getPasswordStrength } from './opPasswordBox.helper';

/**
 * OpPasswordBox
 *
 * Standardized password input with support for:
 * - Password visibility toggle
 * - Top or inside label
 * - Required indicator
 * - Error message
 * - Disabled state
 * - Optional password strength indicator
 *
 * The component manages its own internal state. The `value` prop acts as
 * the initial/preloaded value and will resync whenever it changes.
 */
export default function OpPasswordBox({
  enabled,
  enableStrongPasswordValidation,
  errorMessage,
  label,
  labelPosition,
  showAsterisk,
  value,
  onChange,
  className,
}) {
  const [internalValue, setInternalValue] = useState(value ?? '');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value);
    }
  }, [value]);

  const isDisabled = !enabled;
  const isLabelInside = labelPosition === 'Inside';

  const strength = getPasswordStrength(internalValue);

  const strengthLabel = [
    'Very Weak',
    'Weak',
    'Medium',
    'Strong',
    'Very Strong',
  ][strength];

  const handleChange = (e) => {
    const currentValue = e.target.value;

    setInternalValue(currentValue);

    onChange?.(currentValue);
  };

  return (
    <div
      className={clsx(
        'op-password-wrapper',
        isLabelInside && 'op-password-wrapper-label-inside',
        isDisabled && 'op-password-wrapper-disabled',
        isDisabled
          && isLabelInside
          && 'op-password-wrapper-disabled-label-inside',
      )}
    >
      {label && (
        <label
          className={clsx(
            'op-password-label',
            isLabelInside && 'op-password-label-inside',
          )}
        >
          {label}
          {showAsterisk && <span className="op-password-asterisk">*</span>}
        </label>
      )}

      <div className="op-password-input-container">
        <input
          className={clsx(
            'op-password-input',
            className,
            errorMessage && 'op-password-error-input',
            isLabelInside && 'op-password-label-inside-input',
          )}
          disabled={isDisabled}
          onChange={handleChange}
          type={showPassword ? 'text' : 'password'}
          value={internalValue}
        />

        <button
          className="op-password-eye-button"
          onClick={() => setShowPassword((prev) => !prev)}
          tabIndex={-1}
          type="button"
        >
          <img
            alt={showPassword ? 'Hide Password' : 'Show Password'}
            className="op-password-eye-icon"
            src={showPassword ? eyeSlashIcon : eyeIcon}
          />
        </button>
      </div>

      {enableStrongPasswordValidation && (
        <div className="op-password-strength-container">
          <div className="op-password-strength-bars">
            {[1, 2, 3, 4].map((bar) => (
              <div
                key={bar}
                className={clsx(
                  'op-password-strength-bar',
                  strength >= bar && `strength-${strength}`,
                )}
              />
            ))}
          </div>

          <span className="op-password-strength-label">{strengthLabel}</span>
        </div>
      )}

      {errorMessage && (
        <span className="op-password-error-message">{errorMessage}</span>
      )}
    </div>
  );
}

OpPasswordBox.propTypes = {
  enabled: PropTypes.bool,
  enableStrongPasswordValidation: PropTypes.bool,
  errorMessage: PropTypes.string,
  label: PropTypes.string,
  labelPosition: PropTypes.oneOf(['Top', 'Inside']),
  showAsterisk: PropTypes.bool,
  value: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
};

OpPasswordBox.defaultProps = {
  enabled: true,
  enableStrongPasswordValidation: false,
  errorMessage: undefined,
  label: undefined,
  labelPosition: 'Top',
  showAsterisk: false,
  value: '',
  onChange: undefined,
  className: undefined,
};
